
import {
  computed, defineComponent, PropType, reactive, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { MEDIA_TYPES, SHARE_SETTINGS } from '@/types/enums';
import BottomPopUpItem from '@/components/BottomPopUp/BottomPopUpItem.vue';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import AppSoloCaseSelector from '@/components/AppSoloCaseSelector.vue';
import { ICase, IMedia } from '@/types/interfaces';
import AppSkeletonCaseView from '@/components/skeletons/AppSkeletonCaseView.vue';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import NativeService from '@/services/NativeService';
import ToFlutterService from '@/services/ToFlutterService';
import MixedMediaInfoPopUp from '@/components/BottomPopUp/PopUpTypes/Upload/MixedMediaInfoPopUp.vue';

export default defineComponent({
  components: {
    MixedMediaInfoPopUp,
    BottomPopUpItem,
    AppBottomPopUp,
    AppSoloCaseSelector,
    AppSkeletonCaseView,
  },
  props: {
    file: { type: Object as PropType<IMedia>, required: true },
    type: { type: String as PropType<MEDIA_TYPES>, required: true },
  },
  emits: ['close', 'close-media', 'openMovePopUp', 'deleteMedia', 'saveMediaAsCopy', 'removeSharing', 'onInfo', 'openFileRenamePopUp', 'onShare'],
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const route = useRoute();
    const router = useRouter();
    const driveStore = useDriveStore();
    const isNetwork = computed(() => route.meta.isNetwork || false);
    const isMy = computed(() => props.file?.owner?.accountId === mainStore.user.accountId);
    const allowSave = computed(() => (!isNetwork.value && isMy.value)
      || (props.file?.root && props.file?.shareSettings?.includes(SHARE_SETTINGS.ALLOW_SAVE)));

    const isPhoto = computed(() => props.type === MEDIA_TYPES.PHOTO);

    const caseViewSkeleton = ref(false);
    const caseSelector = ref(false);
    const showInfo = ref(false);

    const createCaseModal = ref();

    const skeletonCase = reactive({ title: '', photosAmount: 0, filesAmount: 0 });

    const downloadUrl = computed(() => process.env.VUE_APP_MEDIA_URL + props.file.downloadUrl);

    function hidePopUp() {
      emit('close');
    }
    function editFile() {
      hidePopUp();
      emit('openFileRenamePopUp');
    }
    function openMovePopUp() {
      emit('openMovePopUp');
    }

    function downloadMedia() {
      if (isPhoto.value) {
        NativeService.downloadMedia(process.env.VUE_APP_MEDIA_URL + props.file.fileUrl);
      } else {
        NativeService.downloadFile(process.env.VUE_APP_MEDIA_URL + props.file.fileUrl, props.file.name);
      }
    }
    function addMediaToCase(iCase: ICase) {
      caseViewSkeleton.value = true;

      skeletonCase.title = iCase.title;
      skeletonCase.filesAmount = iCase.filesAmount || 0;
      skeletonCase.photosAmount = (iCase.photosAmount || 0) + 1;

      driveStore.currentCase.title = iCase.title;
      driveStore.currentCase.photosAmount = skeletonCase.photosAmount;
      driveStore.currentCase.filesAmount = skeletonCase.filesAmount;

      const payload = { id: iCase.id!, fileIds: [driveStore.appPhotoViewMedia?.id as unknown as string] };
      driveStore.addFilesToCase(payload).then((response) => {
        if (response.success) {
          router.push(`/drive/case/my/${iCase.id!}/view`).then(() => {
            // emit('close-media');
            driveStore.appPhotoViewMedia = undefined;
          });
        }
      }).catch(() => {
        caseViewSkeleton.value = false;
      });
    }

    function downloadMediaFlutter() {
      ToFlutterService.downloadMedia(process.env.VUE_APP_MEDIA_URL + props.file.fileUrl, props?.file?.name || '');
    }

    function deleteMedia(): void {
      emit('deleteMedia');
    }

    function onInfo(): void {
      emit('onInfo');
    }

    function saveMediaAsCopy() {
      emit('saveMediaAsCopy');
    }

    function removeSharing() {
      emit('removeSharing');
    }

    return {
      hidePopUp,
      deleteMedia,
      onInfo,
      downloadMedia,
      downloadMediaFlutter,
      openMovePopUp,
      saveMediaAsCopy,
      removeSharing,
      mainStore,
      skeletonCase,
      isPhoto,
      caseViewSkeleton,
      createCaseModal,
      caseSelector,
      downloadUrl,
      isNetwork,
      isMy,
      allowSave,
      showInfo,
      addMediaToCase,
      editFile,
    };
  },
});
